/**
 * PricingPage.js - Public pricing page showing Pickup/League/Commissioner tiers
 */

import React from 'react';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import { useTheme } from '../context/ThemeContext';

const BETA_MODE = process.env.REACT_APP_BETA_MODE === 'true';

function getStyles(colors) {
  return {
    page: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    },
    nav: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1.25rem 2rem',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    logoLink: {
      textDecoration: 'none',
      color: '#fff',
      fontSize: '1.5rem',
      fontWeight: '700',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    navLinks: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
    },
    navLink: {
      color: 'rgba(255, 255, 255, 0.9)',
      textDecoration: 'none',
      fontSize: '0.95rem',
      fontWeight: '500',
      padding: '0.5rem 1rem',
      borderRadius: '8px',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      transition: 'all 0.2s ease',
    },
    header: {
      textAlign: 'center',
      padding: '3rem 2rem 1rem',
      color: '#fff',
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      margin: '0 0 0.75rem',
    },
    subtitle: {
      fontSize: '1.15rem',
      fontWeight: '400',
      opacity: 0.9,
      margin: '0',
    },
    betaBanner: {
      display: 'inline-block',
      background: 'rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)',
      color: '#fff',
      padding: '0.6rem 1.5rem',
      borderRadius: '50px',
      fontSize: '0.95rem',
      fontWeight: '600',
      marginTop: '1.5rem',
      border: '1px solid rgba(255, 255, 255, 0.3)',
    },
    cardsContainer: {
      display: 'flex',
      justifyContent: 'center',
      gap: '1.5rem',
      padding: '2.5rem 2rem 4rem',
      maxWidth: '1100px',
      margin: '0 auto',
      flexWrap: 'wrap',
    },
    card: {
      background: colors.card,
      borderRadius: '16px',
      padding: '2rem',
      width: '320px',
      boxShadow: `0 4px 24px ${colors.shadow}`,
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform 0.2s ease',
      position: 'relative',
    },
    cardPro: {
      background: colors.card,
      borderRadius: '16px',
      padding: '2rem',
      width: '320px',
      boxShadow: '0 8px 32px rgba(118, 75, 162, 0.3)',
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform 0.2s ease',
      position: 'relative',
      border: '2px solid #764ba2',
    },
    popularBadge: {
      position: 'absolute',
      top: '-12px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
      padding: '0.3rem 1.25rem',
      borderRadius: '50px',
      fontSize: '0.8rem',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      whiteSpace: 'nowrap',
    },
    planName: {
      fontSize: '1.3rem',
      fontWeight: '700',
      color: colors.text,
      margin: '0 0 0.5rem',
    },
    priceRow: {
      display: 'flex',
      alignItems: 'baseline',
      gap: '0.25rem',
      margin: '0 0 0.25rem',
    },
    price: {
      fontSize: '2.5rem',
      fontWeight: '700',
      color: colors.text,
    },
    pricePeriod: {
      fontSize: '1rem',
      color: colors.textSecondary,
      fontWeight: '500',
    },
    annualPrice: {
      fontSize: '0.85rem',
      color: colors.textSecondary,
      margin: '0 0 1.5rem',
    },
    annualPriceFree: {
      fontSize: '0.85rem',
      color: colors.textSecondary,
      margin: '0 0 1.5rem',
      visibility: 'hidden',
    },
    divider: {
      border: 'none',
      borderTop: `1px solid ${colors.border}`,
      margin: '0 0 1.25rem',
    },
    featureList: {
      listStyle: 'none',
      padding: 0,
      margin: '0 0 2rem',
      flex: 1,
    },
    feature: {
      padding: '0.4rem 0',
      fontSize: '0.95rem',
      color: colors.textSecondary,
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.5rem',
      lineHeight: '1.4',
    },
    checkmark: {
      color: colors.brand,
      fontWeight: '700',
      flexShrink: 0,
      marginTop: '1px',
    },
    ctaButton: {
      display: 'block',
      textAlign: 'center',
      padding: '0.85rem 1.5rem',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      textDecoration: 'none',
      transition: 'all 0.2s ease',
      border: `2px solid ${colors.brand}`,
      color: colors.brand,
      background: colors.card,
    },
    ctaButtonPrimary: {
      display: 'block',
      textAlign: 'center',
      padding: '0.85rem 1.5rem',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      textDecoration: 'none',
      transition: 'all 0.2s ease',
      border: 'none',
      color: '#fff',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    footer: {
      textAlign: 'center',
      padding: '0 2rem 3rem',
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: '0.9rem',
    },
  };
}

function PricingCard({ name, tagline, price, period, annual, features, isPro, cta, styles }) {
  return (
    <div style={isPro ? styles.cardPro : styles.card}>
      {isPro && <div style={styles.popularBadge}>Most Popular</div>}
      <div style={styles.planName}>{name}</div>
      {tagline && <div style={{ fontSize: '0.85rem', color: styles.feature.color, margin: '-0.25rem 0 0.5rem', lineHeight: '1.4' }}>{tagline}</div>}
      {BETA_MODE ? (
        <>
          <div style={styles.priceRow}>
            <span style={{ ...styles.price, textDecoration: price !== '$0' ? 'line-through' : 'none', opacity: price !== '$0' ? 0.4 : 1 }}>{price}</span>
            {period && <span style={{ ...styles.pricePeriod, textDecoration: price !== '$0' ? 'line-through' : 'none', opacity: price !== '$0' ? 0.4 : 1 }}>{period}</span>}
            {price !== '$0' && <span style={{ ...styles.price, marginLeft: '0.5rem' }}>$0</span>}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#38a169', fontWeight: 600, margin: '0.25rem 0 1.5rem' }}>
            Free during beta
          </div>
        </>
      ) : (
        <>
          <div style={styles.priceRow}>
            <span style={styles.price}>{price}</span>
            {period && <span style={styles.pricePeriod}>{period}</span>}
          </div>
          <div style={annual ? styles.annualPrice : styles.annualPriceFree}>
            {annual || '\u00A0'}
          </div>
        </>
      )}
      <hr style={styles.divider} />
      <ul style={styles.featureList}>
        {features.map((feat, i) => (
          <li key={i} style={styles.feature}>
            <span style={styles.checkmark}>{'\u2713'}</span>
            <span>{feat}</span>
          </li>
        ))}
      </ul>
      <Link to="/" style={isPro ? styles.ctaButtonPrimary : styles.ctaButton}>
        {BETA_MODE ? 'Request Access' : cta}
      </Link>
    </div>
  );
}

export default function PricingPage() {
  const { colors } = useTheme();
  const styles = getStyles(colors);

  return (
    <div style={styles.page}>
      <nav style={styles.nav}>
        <Link to="/" style={styles.logoLink}>
          <Logo size="1.5rem" /> LeagueMaker
        </Link>
        <div style={styles.navLinks}>
          <Link to="/" style={styles.navLink}>Log In</Link>
        </div>
      </nav>

      <div style={styles.header}>
        <h1 style={styles.title}>Simple, transparent pricing</h1>
        <p style={styles.subtitle}>
          Everything you need to run your league, from casual to competitive.
        </p>
        <div style={styles.betaBanner}>
          Currently in Beta — all features free during the beta period!
        </div>
      </div>

      <div style={styles.cardsContainer}>
        <PricingCard
          name="Pickup"
          tagline="Just getting started. Perfect for your first league."
          price="$0"
          period="/mo"
          features={[
            '1 league, up to 8 teams',
            'Fair time-slot scheduling algorithm',
            'PDF export (with DRAFT watermark)',
            'Public league page with calendar sync',
            'PWA support (add to home screen)',
          ]}
          cta="Get Started Free"
          styles={styles}
        />
        <PricingCard
          name="League"
          tagline="Everything you need to run a great season."
          price="$15"
          period="/mo"
          annual="$150/yr (save $30)"
          features={[
            'Everything in Pickup, plus:',
            'Unlimited teams',
            'No watermark on PDFs',
            'Email notifications to captains',
            'Captain portal with magic links',
            'Venue management with conflict detection',
            'Online registration forms',
            'Registration management & CSV export',
            'Payment collection via Stripe',
          ]}
          cta="Get Started"
          styles={styles}
        />
        <PricingCard
          name="Commissioner"
          tagline="For the organizer who runs it all."
          price="$25"
          period="/mo"
          annual="$250/yr (save $50)"
          isPro
          features={[
            'Everything in League, plus:',
            'Unlimited leagues',
            'Score entry & live standings',
            'Captain score submission (with approval)',
            'Playoff brackets',
            'Season archiving & history',
            'Referee management with portal',
            'Branded league website (custom subdomain)',
            'Announcements, sponsors & photo gallery',
            'Priority support',
          ]}
          cta="Get Started"
          styles={styles}
        />
      </div>

      {BETA_MODE && (
        <div style={{ textAlign: 'center', padding: '0 2rem 1.5rem', maxWidth: 700, margin: '0 auto' }}>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
            All platform features are free during our beta period. You only pay standard payment processing fees when collecting registration fees from players.
          </p>
        </div>
      )}

      <div style={styles.footer}>
        <div style={{ marginBottom: '0.5rem' }}>
          Questions? <Link to="/" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Contact us</Link>
        </div>
        <div>
          <Link to="/terms" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', fontSize: '0.85rem' }}>Terms</Link>
          <span style={{ margin: '0 0.5rem', color: 'rgba(255, 255, 255, 0.4)' }}>|</span>
          <Link to="/privacy" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', fontSize: '0.85rem' }}>Privacy</Link>
        </div>
      </div>
    </div>
  );
}
